import { useState } from "react";
import { ReactLenis } from "lenis/react";
import { useSearchParams } from "react-router-dom";
import { Play, User, Users } from "lucide-react";
import { Toaster } from "sonner";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { ShowreelProvider } from "@/components/site/Showreel";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Footer } from "@/components/site/Footer";
import { ArchiveHeader } from "@/components/site/ArchiveHeader";
import { AwardLabel, filmAward, laurelSrc, NameLink, prizeCardStyle } from "@/components/site/FestivalGraphics";
import { FestivalFilmModal } from "@/components/site/FestivalFilmModal";
import { JoinDialog } from "@/components/site/JoinDialog";
import { editionVisual, POSTER_BLOCK, posterImgStyle, HScroll } from "@/components/site/FestivalShowcase";
import { resolveUrl, galleryCover } from "@/lib/media";
import { resolveTheme } from "@/lib/festivalTheme";

const sortEds = (eds) => (eds || []).filter((e) => e && !e.hidden).sort((a, b) => (parseInt(b.year, 10) || 0) - (parseInt(a.year, 10) || 0));
const visFilms = (ed) => (ed?.films || [])
  .filter((f) => f && !f.hidden && (f.title || f.poster))
  .map((f, i) => ({ ...f, _i: i }))
  .sort((a, b) => (a.order ?? a._i) - (b.order ?? b._i));

const PalmaresBody = () => {
  const { content } = useContent();
  const [params] = useSearchParams();
  const archive = content.festivalArchive || {};
  const eds = sortEds(archive.editions);
  const withPalmares = eds.filter((ed) => visFilms(ed).some((f) => filmAward(f)));
  const [year, setYear] = useState(params.get("year") || (withPalmares[0] || eds[0])?.year);
  const [featKey, setFeatKey] = useState(null);
  const [film, setFilm] = useState(null);
  const [joinOpen, setJoinOpen] = useState(false);

  const ed = eds.find((e) => String(e.year) === String(year)) || eds[0];
  const films = visFilms(ed);
  const entries = [];
  for (const f of films) {
    const aw = filmAward(f);
    if (aw) entries.push({ key: f.id, award: aw, film: f, poster: f.poster || galleryCover(f.media), cover: f.coverImage, laureate: f.laureate });
  }
  // Grand Prix (principal) is featured by default; visitor can select any laureate.
  const defaultEntry = entries.find((e) => e.award.principal) || entries[0] || null;
  const featured = entries.find((e) => e.key === featKey) || defaultEntry;
  const themeVars = resolveTheme(archive.theme, ed);

  return (
    <div className="App festival-universe min-h-screen flex flex-col" style={{ ...themeVars, backgroundColor: "var(--fx-bg)", color: "var(--fx-text)" }}>
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <ArchiveHeader brand={content.brand || {}} edition={ed} onApply={() => setJoinOpen(true)} active="palmares" />

      <main className="flex-1 pt-[110px] pb-16" data-testid="festival-palmares-page">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-electric mb-2 font-semibold">Karams Festival</div>
          <h1 className="fx-display font-serif text-4xl md:text-6xl text-white tracking-tight mb-8">Palmarès</h1>

          <div className="flex flex-wrap gap-2 mb-10" data-testid="palmares-years">
            {eds.map((e) => (
              <button key={e.id || e.year} onClick={() => setYear(e.year)} data-testid={`palmares-year-${e.year}`}
                className={`px-4 h-9 rounded-md text-[12px] tracking-[0.1em] uppercase transition-colors ${String(e.year) === String(year) ? "bg-electric font-semibold" : "border border-white/15 text-white/70 hover:text-electric"}`}
                style={String(e.year) === String(year) ? { color: "var(--fx-on-primary)" } : undefined}>{e.year}</button>
            ))}
          </div>

          {entries.length === 0 ? (
            <p className="text-secondary2 font-light">Le palmarès de cette édition sera dévoilé prochainement.</p>
          ) : (
            <>
              {/* Featured laureate — interactive, cinematic habillage */}
              {featured && (() => {
                const c = featured.award.color;
                const awText = c === "bronze" ? "#cd8f52" : c === "white" ? "#dfe3ea" : "#e7c56a";
                const brandLogo = content.brand?.logo;
                const useCover = !!featured.cover;
                const gwStyle = prizeCardStyle(featured.award, archive.prizeStyles) || {};
                return (
                <div style={gwStyle} className={`relative mb-12 md:mb-14 rounded-2xl overflow-hidden border ${gwStyle.borderColor ? "" : "border-white/[0.06]"}`} data-testid="palmares-grand-winner">
                  <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                    <div className="absolute inset-0" style={{ background: `radial-gradient(120% 90% at 50% 0%, ${awText}22, transparent 60%)` }} />
                    <img src={laurelSrc(c)} onError={(ev) => { ev.currentTarget.src = laurelSrc("gold"); }} alt="" className="absolute -left-20 top-1/2 -translate-y-1/2 h-[130%] opacity-[0.06] object-contain" />
                    <img src={laurelSrc(c)} onError={(ev) => { ev.currentTarget.src = laurelSrc("gold"); }} alt="" className="absolute -right-20 top-1/2 -translate-y-1/2 h-[130%] opacity-[0.06] object-contain -scale-x-100" />
                    {brandLogo && <img src={resolveUrl(brandLogo)} alt="" className="absolute right-5 bottom-5 h-20 md:h-36 opacity-[0.05] object-contain" />}
                  </div>
                  <div className="relative grid grid-cols-1 md:grid-cols-[minmax(240px,44%)_1fr] gap-8 md:gap-12 items-center p-6 md:p-10">
                    <button onClick={() => setFilm({ film: featured.film, year: ed.year })} className="group relative block mx-auto w-full" data-testid="palmares-grand-poster">
                      <div className="absolute -inset-5 rounded-[36px] blur-3xl opacity-40 pointer-events-none" style={{ background: `radial-gradient(circle, ${awText}55, transparent 70%)` }} />
                      <div className={`relative ${useCover ? "aspect-[16/10]" : POSTER_BLOCK} w-full ${useCover ? "max-w-[560px]" : "max-w-[300px]"} mx-auto rounded-xl overflow-hidden border border-white/15 bg-[#0e0e0e] shadow-2xl`}>
                        {(useCover ? featured.cover : featured.poster)
                          ? <img src={resolveUrl(useCover ? featured.cover : featured.poster)} alt={featured.film.title} className="w-full h-full object-cover" style={useCover ? undefined : posterImgStyle(featured.film)} />
                          : <div className="w-full h-full flex items-center justify-center text-white/30 text-sm px-3 text-center">{featured.film.title}</div>}
                      </div>
                    </button>
                    <div className="text-center md:text-left flex flex-col items-center md:items-start">
                      <div className="text-[11px] tracking-[0.4em] uppercase font-semibold mb-4" style={{ color: awText }} data-testid="palmares-grand-edition">Édition {ed.year}</div>
                      <div className="flex items-center gap-4 md:gap-6 mb-6">
                        <img src={laurelSrc(c)} onError={(ev) => { ev.currentTarget.src = laurelSrc("gold"); }} alt="" aria-hidden="true" className="h-11 md:h-16 object-contain shrink-0" />
                        <h2 className="fx-prize-name font-bold uppercase tracking-[0.04em] text-2xl md:text-4xl leading-none" style={{ color: awText }} data-testid="palmares-grand-award">{featured.award.name}</h2>
                        <img src={laurelSrc(c)} onError={(ev) => { ev.currentTarget.src = laurelSrc("gold"); }} alt="" aria-hidden="true" className="h-11 md:h-16 object-contain shrink-0 -scale-x-100" />
                      </div>
                      <h3 className="fx-palmares-title font-serif text-4xl md:text-6xl text-white leading-[1.05] mb-5" data-testid="palmares-grand-title">{featured.film.title}</h3>
                      {(featured.laureate || featured.film.director || featured.film.team) && (
                        <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-2 text-sm text-white/85 mb-6">
                          {(featured.laureate || featured.film.director) && <span className="inline-flex items-center gap-1.5"><User size={14} className="text-electric" /> Réalisé par <NameLink name={featured.laureate || featured.film.director} url={featured.film.directorUrl} /></span>}
                          {featured.film.team && <span className="inline-flex items-center gap-1.5"><Users size={14} className="text-electric" /> Structure : <NameLink name={featured.film.team} url={featured.film.teamUrl} /></span>}
                        </div>
                      )}
                      {featured.award.juryNote && <blockquote className="text-white/80 font-light leading-relaxed mb-8 max-w-2xl" data-testid="palmares-grand-jury-note">{featured.award.juryNote}</blockquote>}
                      <button onClick={() => setFilm({ film: featured.film, year: ed.year })} data-testid="palmares-grand-discover" className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full border border-electric text-electric text-[12px] tracking-[0.15em] uppercase font-semibold hover:bg-electric/10 transition-colors"><Play size={15} /> Découvrir le film</button>
                    </div>
                  </div>
                </div>
                );
              })()}

              {/* All laureates — horizontal carousel; click selects the featured above */}
              <div>
                <h2 className="fx-section font-serif text-2xl md:text-3xl text-white mb-6" data-testid="palmares-others-title">Palmarès {ed.year}</h2>
                <HScroll testid="palmares-scroll">
                  {entries.map((e) => {
                    const active = featured && e.key === featured.key;
                    const pStyle = prizeCardStyle(e.award, archive.prizeStyles) || {};
                    return (
                      <button key={e.key} onClick={() => { setFeatKey(e.key); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`palmares-entry-${e.key}`}
                        style={{ ...pStyle, ...(active ? { outline: "2px solid var(--fx-primary)", outlineOffset: "-2px" } : {}) }}
                        className={`snap-start shrink-0 w-[200px] text-left rounded-xl border ${pStyle.borderColor ? "" : "border-white/12"} bg-white/[0.02] p-3 flex flex-col transition-[outline,box-shadow]`}>
                        <div className="h-10 mb-2.5 flex items-center justify-center px-1 overflow-hidden"><AwardLabel name={e.award.name} color={e.award.color} size={e.award.size} wrap /></div>
                        <div className={`relative ${POSTER_BLOCK} w-full rounded-md overflow-hidden border border-white/10 bg-[#0e0e0e]`}>
                          {e.poster ? <img src={resolveUrl(e.poster)} alt={e.film.title} loading="lazy" className="w-full h-full object-cover" style={posterImgStyle(e.film)} /> : <div className="w-full h-full flex items-center justify-center text-white/30 text-xs px-2 text-center">{e.film.title}</div>}
                        </div>
                        <div className="mt-3 space-y-1 min-h-[38px]">
                          {(e.laureate || e.film.director) && <div className="text-[11px] text-white/70 flex items-center gap-1.5"><User size={12} className="text-electric shrink-0" /> <span className="line-clamp-1">Réalisé par {e.laureate || e.film.director}</span></div>}
                          {e.film.team && <div className="text-[11px] text-secondary2 flex items-center gap-1.5"><Users size={12} className="text-electric shrink-0" /> <span className="line-clamp-1">{e.film.team}</span></div>}
                        </div>
                      </button>
                    );
                  })}
                </HScroll>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
      <FestivalFilmModal film={film?.film} editionYear={film?.year} onClose={() => setFilm(null)} />
      <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} type="festival" refId="festival"
        refTitle="Karams Festival" roles={(content.festival || {}).joinRoles || []}
        heading="Soumettre ma candidature" intro="Candidatez, devenez bénévole ou partenaire du Karams Festival." />
    </div>
  );
};

export default function FestivalPalmaresPage() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <ShowreelProvider>
          <Analytics />
          <PalmaresBody />
        </ShowreelProvider>
      </ReactLenis>
    </ContentProvider>
  );
}
